// ============================================================
// systems/shop.js — Shop system (upgrades, auto-clickers)
// ============================================================
import { state } from '../state.js';
import { dom, closeAllPanels } from '../dom.js';
import { MAX_LEVEL, BASE_AUTO_INTERVAL, AUTO_CLICKER_IMG } from '../constants.js';
import { checkPhaseTransitions } from '../phases.js';
import { playPurchaseSound as playClickPowerPurchaseSound, playPanelOpenSound, playPanelCloseSound } from '../audio/sfx.js';

// ---- helpers ----

export function getAutoInterval() {
  return BASE_AUTO_INTERVAL - (state.autoSpeedLevel * 50);
}

export function updateShopUI() {
  dom.clickPowerLevelEl.textContent = 'Level ' + state.clickPowerLevel + '/' + MAX_LEVEL;
  dom.clickPowerDisplay.textContent = '+' + state.clickPower + ' per click' +
    (state.autoClickerCount > 0 ? ' | ' + state.autoClickerCount + ' auto' : '');

  dom.autoClickerLevelEl.textContent = 'Level ' + state.autoClickerCount + '/' + MAX_LEVEL;

  // Click Power max check
  if (state.clickPowerLevel >= MAX_LEVEL) {
    dom.shopClickPower.classList.add('maxed');
    dom.shopClickPower.classList.remove('cant-afford');
  } else {
    dom.shopClickPower.classList.remove('maxed');
    dom.clickPowerPriceEl.textContent = state.clickPowerCost + ' clicks';
    if (state.score >= state.clickPowerCost) {
      dom.shopClickPower.classList.remove('cant-afford');
    } else {
      dom.shopClickPower.classList.add('cant-afford');
    }
  }

  // Auto Clicker max check
  if (state.autoClickerCount >= MAX_LEVEL) {
    dom.shopAutoClicker.classList.add('maxed');
    dom.shopAutoClicker.classList.remove('cant-afford');
  } else {
    dom.shopAutoClicker.classList.remove('maxed');
    dom.autoClickerPriceEl.textContent = state.autoClickerCost + ' clicks';
    if (state.score >= state.autoClickerCost) {
      dom.shopAutoClicker.classList.remove('cant-afford');
    } else {
      dom.shopAutoClicker.classList.add('cant-afford');
    }
  }

  // Auto Speed max check
  dom.autoSpeedLevelEl.textContent = 'Level ' + state.autoSpeedLevel + '/' + MAX_LEVEL;
  if (state.autoSpeedLevel >= MAX_LEVEL) {
    dom.shopAutoSpeed.classList.add('maxed');
    dom.shopAutoSpeed.classList.remove('cant-afford');
  } else {
    dom.shopAutoSpeed.classList.remove('maxed');
    dom.autoSpeedPriceEl.textContent = state.autoSpeedCost + ' clicks';
    if (state.score >= state.autoSpeedCost) {
      dom.shopAutoSpeed.classList.remove('cant-afford');
    } else {
      dom.shopAutoSpeed.classList.add('cant-afford');
    }
  }

  // Mega Click — always visible, locked until Click Power is maxed
  if (state.clickPowerLevel >= MAX_LEVEL) {
    dom.shopMegaClick.classList.remove('locked');
    dom.megaClickLevelEl.textContent = 'Level ' + state.megaClickLevel + '/' + MAX_LEVEL;
    if (state.megaClickLevel >= MAX_LEVEL) {
      dom.shopMegaClick.classList.add('maxed');
      dom.shopMegaClick.classList.remove('cant-afford');
    } else {
      dom.shopMegaClick.classList.remove('maxed');
      dom.megaClickPriceEl.textContent = state.megaClickCost + ' clicks';
      if (state.score >= state.megaClickCost) {
        dom.shopMegaClick.classList.remove('cant-afford');
      } else {
        dom.shopMegaClick.classList.add('cant-afford');
      }
    }
  } else {
    dom.shopMegaClick.classList.add('locked');
  }
}

// ---- auto-clicker mouse ----

export function spawnAutoMouse() {
  const mouseIdx = state.autoMouseElements.length;
  const img = document.createElement('img');
  img.src = AUTO_CLICKER_IMG;
  img.className = 'auto-mouse';
  img.draggable = false;
  document.body.appendChild(img);
  state.autoMouseElements.push({
    el: img,
    angle: mouseIdx * (Math.PI * 2 / 8),
    speed: 0.008 + Math.random() * 0.006,
    radius: 140 + mouseIdx * 25,
  });

  // Start animation loop if first mouse
  if (state.autoMouseElements.length === 1) {
    requestAnimationFrame(animateAutoMice);
  }
}

export function animateAutoMice() {
  if (state.paused) { requestAnimationFrame(animateAutoMice); return; }
  const smileyRect = dom.smiley.getBoundingClientRect();
  const cx = smileyRect.left + smileyRect.width / 2;
  const cy = smileyRect.top + smileyRect.height / 2;

  state.autoMouseElements.forEach(m => {
    m.angle += m.speed;
    const x = cx + Math.cos(m.angle) * m.radius - 18;
    const y = cy + Math.sin(m.angle) * m.radius * 0.6 - 18;
    const rot = Math.atan2(cy - (y + 18), cx - (x + 18)) * (180 / Math.PI) + 90;
    m.el.style.transform = `translate(${x}px, ${y}px) rotate(${rot}deg)`;

    // Show a little click pulse sometimes (reduced in phase 3)
    if (Math.random() < (state.phase3Active ? 0.005 : 0.015)) {
      const pulse = document.createElement('div');
      pulse.className = 'auto-mouse-click';
      pulse.style.left = (x + 15) + 'px';
      pulse.style.top = (y + 15) + 'px';
      document.body.appendChild(pulse);
      setTimeout(() => pulse.remove(), 400);
    }
  });

  if (state.autoMouseElements.length > 0) {
    requestAnimationFrame(animateAutoMice);
  }
}

// ---- auto-click interval management ----

function autoClickTick() {
  if (state.dead || state.paused || state.inMenu || state.awaitingFirstClick) return;
  state.score += 1;
  dom.scoreEl.textContent = state.score;
  updateShopUI();
  checkPhaseTransitions();
}

export function restartAutoIntervals() {
  state.autoClickerIntervals.forEach(id => clearInterval(id));
  state.autoClickerIntervals = [];
  const interval = getAutoInterval();
  for (let i = 0; i < state.autoClickerCount; i++) {
    const intId = setInterval(autoClickTick, interval);
    state.autoClickerIntervals.push(intId);
  }
}

// ---- init ----

export function initShop() {
  // Open / close
  dom.shopBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const wasOpen = dom.shopEl.classList.contains('open');
    closeAllPanels();
    if (wasOpen) {
      playPanelCloseSound();
    } else {
      dom.shopEl.classList.add('open');
      playPanelOpenSound();
    }
  });
  dom.shopCloseBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    dom.shopEl.classList.remove('open');
    playPanelCloseSound();
  });

  // --- Click Power purchase ---
  dom.shopClickPower.addEventListener('click', (e) => {
    e.stopPropagation();
    if (state.clickPowerLevel >= MAX_LEVEL) return;
    if (state.score < state.clickPowerCost) return;

    state.score -= state.clickPowerCost;
    state.clickPower++;
    state.clickPowerLevel++;
    state.clickPowerCost *= 2;

    dom.scoreEl.textContent = state.score;
    updateShopUI();

    dom.clickPowerBoughtEl.style.display = 'block';
    setTimeout(() => dom.clickPowerBoughtEl.style.display = 'none', 1200);

    // Purchase sound
    playClickPowerPurchaseSound();
  });

  // --- Auto Clicker purchase ---
  dom.shopAutoClicker.addEventListener('click', (e) => {
    e.stopPropagation();
    if (state.autoClickerCount >= MAX_LEVEL) return;
    if (state.score < state.autoClickerCost) return;

    state.score -= state.autoClickerCost;
    state.autoClickerCount++;
    state.autoClickerCost *= 2;

    dom.scoreEl.textContent = state.score;
    updateShopUI();

    dom.autoClickerBoughtEl.style.display = 'block';
    setTimeout(() => dom.autoClickerBoughtEl.style.display = 'none', 1200);

    // Purchase sound
    if (state.audioCtx) {
      const now = state.audioCtx.currentTime;
      [392, 523.25, 659.25].forEach((freq, i) => {
        const osc = state.audioCtx.createOscillator();
        osc.type = 'triangle';
        osc.frequency.value = freq;
        const env = state.audioCtx.createGain();
        env.gain.setValueAtTime(0, now + i * 0.1);
        env.gain.linearRampToValueAtTime(0.1, now + i * 0.1 + 0.03);
        env.gain.exponentialRampToValueAtTime(0.001, now + i * 0.1 + 0.3);
        osc.connect(env).connect(state.audioCtx.destination);
        osc.start(now + i * 0.1);
        osc.stop(now + i * 0.1 + 0.35);
      });
    }

    // Spawn a floating cartoon mouse
    spawnAutoMouse();

    // Start auto clicking interval for this mouse
    const intId = setInterval(autoClickTick, getAutoInterval());
    state.autoClickerIntervals.push(intId);
  });

  // --- Auto Speed purchase ---
  dom.shopAutoSpeed.addEventListener('click', (e) => {
    e.stopPropagation();
    if (state.autoSpeedLevel >= MAX_LEVEL) return;
    if (state.score < state.autoSpeedCost) return;

    state.score -= state.autoSpeedCost;
    state.autoSpeedLevel++;
    state.autoSpeedCost *= 2;

    dom.scoreEl.textContent = state.score;
    updateShopUI();

    // Restart all auto clicker intervals with new speed
    restartAutoIntervals();

    dom.autoSpeedBoughtEl.style.display = 'block';
    setTimeout(() => dom.autoSpeedBoughtEl.style.display = 'none', 1200);

    // Purchase sound
    if (state.audioCtx) {
      const now = state.audioCtx.currentTime;
      [440, 587.33, 880].forEach((freq, i) => {
        const osc = state.audioCtx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const env = state.audioCtx.createGain();
        env.gain.setValueAtTime(0, now + i * 0.06);
        env.gain.linearRampToValueAtTime(0.12, now + i * 0.06 + 0.02);
        env.gain.exponentialRampToValueAtTime(0.001, now + i * 0.06 + 0.2);
        osc.connect(env).connect(state.audioCtx.destination);
        osc.start(now + i * 0.06);
        osc.stop(now + i * 0.06 + 0.25);
      });
    }
  });

  // --- Mega Click purchase ---
  dom.shopMegaClick.addEventListener('click', (e) => {
    e.stopPropagation();
    if (state.megaClickLevel >= MAX_LEVEL) return;
    if (state.score < state.megaClickCost) return;

    state.score -= state.megaClickCost;
    state.megaClickLevel++;
    state.clickPower += 5;
    state.megaClickCost *= 2;

    dom.scoreEl.textContent = state.score;
    updateShopUI();

    dom.megaClickBoughtEl.style.display = 'block';
    setTimeout(() => dom.megaClickBoughtEl.style.display = 'none', 1200);

    // Purchase sound — big impact
    if (state.audioCtx) {
      const now = state.audioCtx.currentTime;
      [261.63, 523.25, 1046.5].forEach((freq, i) => {
        const osc = state.audioCtx.createOscillator();
        osc.type = 'sawtooth';
        osc.frequency.value = freq;
        const env = state.audioCtx.createGain();
        env.gain.setValueAtTime(0, now + i * 0.05);
        env.gain.linearRampToValueAtTime(0.1, now + i * 0.05 + 0.02);
        env.gain.exponentialRampToValueAtTime(0.001, now + i * 0.05 + 0.3);
        osc.connect(env).connect(state.audioCtx.destination);
        osc.start(now + i * 0.05);
        osc.stop(now + i * 0.05 + 0.35);
      });
    }
  });

  // Mobile: swipe-down-to-close bottom drawer
  if (window.matchMedia('(max-width: 768px)').matches) {
    let startY = 0, currentY = 0, dragging = false;

    dom.shopEl.addEventListener('touchstart', (e) => {
      const rect = dom.shopEl.getBoundingClientRect();
      if (e.touches[0].clientY - rect.top > 40) return;
      startY = e.touches[0].clientY;
      dragging = true;
      dom.shopEl.style.transition = 'none';
    }, { passive: true });

    dom.shopEl.addEventListener('touchmove', (e) => {
      if (!dragging) return;
      currentY = e.touches[0].clientY;
      const diff = currentY - startY;
      if (diff > 0) dom.shopEl.style.transform = `translateY(${diff}px)`;
    }, { passive: true });

    dom.shopEl.addEventListener('touchend', () => {
      if (!dragging) return;
      dragging = false;
      dom.shopEl.style.transition = '';
      if (currentY - startY > 80) dom.shopEl.classList.remove('open');
      dom.shopEl.style.transform = '';
    });
  }
}
